import {
  CustomTableCell,
  CustomTableHead,
  FormCheckbox,
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormLabel,
  FormMonthYearPicker,
  FormSectionHeader,
  FormSelect,
  Heading,
  Loader,
} from "@/components";
import useAddEdit from "./useAddEdit";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Input,
  Label,
  Switch,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/components/ui";
import {
  AMOUNT,
  BANK_CONF,
  BANK_INFO,
  BILLING_MONTH_YEAR,
  CASH_APP_FILE,
  CASH_APP_ID,
  CHEQUE_NO,
  CLEARING,
  COMBINE,
  COMMENT,
  CURRENCY,
  CUSTOMER_ACC,
  DATE,
  DOCUMENT_ID,
  DRCR,
  GL_ACCOUNT,
  LINE_ITEM_TEXT,
  LINE_ITEM_TYPE,
  LINE_ITEMS,
  OFFSET_ACC,
  OL_DATE,
  OL_DOC_LI,
  OL_REF,
  PAYMENT_METHOD,
  POSTING_DATE,
  POSTING_MONTH,
  SUB_LEDGER,
  YEAR,
} from "./consts";
import processingGif from "@/assets/processing.json";
import fileUploadError from "@/assets/fileUploadError.png";
import Lottie from "lottie-react";
import moment from "moment";
import { cn, formatAmount, MONTHS } from "@/lib/utils";
import { Download } from "lucide-react";

const AddEdit = () => {
  const {
    breadcrumbData,
    onBackClick,
    form,
    requiredFields,
    isDocumentTypeLoading,
    isLoadingGlMaster,
    breadcrumbTitle,
    keys,
    documentTypes,
    onCancel,
    onSubmit,
    cashAppFileRef,
    isView,
    isAddPending,
    isAddCashAppError,
    balanceAmount,
    isPostPending,
    isCashAppLoading,
    isPosted,
    isEntryView,
    setIsEntryView,
    isAttachmentLoading,
    onAttachmentClicked,
    hasPostPermission,
    hasAddPermission,
    formData
  } = useAddEdit();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6 !pt-0">
      <div className="flex flex-col lg:flex-row lg:items-center w-full justify-between sticky top-[10px] z-10 bg-g-10">
        <Heading
          title={breadcrumbTitle}
          breadcrumbData={breadcrumbData}
          showBack
          onBackClick={onBackClick}
        />
        <div className="grid grid-cols-1 mt-[20px] gap-[10px]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
            <div className="relative">
              <FormLabel>
                {"Posting Period"}
                <span className="text-red-700">{"*"}</span>
              </FormLabel>
              <Input
                name={POSTING_MONTH}
                placeholder="Please enter"
                value={MONTHS[form.watch(POSTING_MONTH) - 1]}
                disabled
              />
            </div>
            <div className="relative">
              <FormLabel>
                {"Posting Year"}
                <span className="text-red-700">{"*"}</span>
              </FormLabel>
              <Input
                name={YEAR}
                placeholder="Please enter"
                value={form.watch(YEAR)}
                disabled
              />
            </div>
          </div>
          {isPosted ? (
            <div className="flex items-center justify-center max-w-[300px]">
              Balance Amount:{"  "}
              <span
                className={cn(
                  balanceAmount < 0 ? "text-red-600" : "text-green-600",
                  "ml-2"
                )}
              >
                {formatAmount(balanceAmount, 2)}
              </span>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>

      {isLoadingGlMaster ||
      isDocumentTypeLoading ||
      isCashAppLoading ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white mt-[10px] rounded-lg px-[30px] py-[15px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] items-center">
                <FormSectionHeader>Document Header</FormSectionHeader>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-[20px]">
                <FormSelect
                  key={keys.documentTypes}
                  name={DOCUMENT_ID}
                  control={form.control}
                  label="Document Type"
                  placeholder="Please select"
                  error={errors[DOCUMENT_ID]?.message}
                  isRequired={requiredFields.includes(DOCUMENT_ID)}
                  options={(documentTypes || [])?.map((data: any) => ({
                    value: data?.documentTypeId?.toString(),
                    label: data?.documentType,
                  }))}
                  disabled
                />
                {formData[CASH_APP_ID] ? <FormInput
                  name={CASH_APP_ID}
                  control={form.control}
                  label="Document Id"
                  placeholder="Please enter"
                  error={errors[CASH_APP_ID]?.message}
                  isRequired={requiredFields.includes(CASH_APP_ID)}
                  disabled
                /> : <></>}
                <FormMonthYearPicker
                  name={BILLING_MONTH_YEAR}
                  control={form.control}
                  label="Billing Month/Year"
                  placeholder="Select"
                  error={errors[BILLING_MONTH_YEAR]?.message}
                  isRequired
                  disabled={isView}
                  onClear={() => form.setValue(BILLING_MONTH_YEAR, null)}
                />
                {/* <FormFileInput
                  fileInputRef={attachmentRef}
                  name={ATTACHMENT_FILE}
                  control={form.control}
                  label="Attachment File"
                  error={errors[ATTACHMENT_FILE]?.message}
                  isRequired
                  accept="excel"
                  disabled={isView}
                /> */}
               <div className="flex flex-row items-center gap-2">
                  <FormFileInput
                    fileInputRef={cashAppFileRef}
                    name={CASH_APP_FILE}
                    control={form.control}
                    label="Upload File"
                    error={errors[CASH_APP_FILE]?.message}
                    isRequired
                    accept="excel"
                    disabled={isView}
                  />
                  <Button type="button" variant="outline" onClick={onAttachmentClicked} disabled={isAttachmentLoading}>
                    {isAttachmentLoading ? <Loader variant="secondary" /> : <></>} <Download />
                  </Button>
                </div>
                <FormDatePicker
                  name={POSTING_DATE}
                  control={form.control}
                  label="Posting Date"
                  placeholder="Please select"
                  error={errors[POSTING_DATE]?.message}
                  isRequired
                  onChange={() => {
                    form.setValue(
                      YEAR,
                      moment(form.getValues()[POSTING_DATE]).format("YYYY")
                    );
                    form.setValue(
                      POSTING_MONTH,
                      moment(form.getValues()[POSTING_DATE]).format("MM")
                    );
                  }}
                  disabled={isView}
                />
                <FormInput
                  name={COMMENT}
                  control={form.control}
                  label="Header Text"
                  placeholder="Please enter"
                  error={errors[COMMENT]?.message}
                  isRequired={requiredFields.includes(COMMENT)}
                  disabled={isView}
                />
              </div>
            </div>
            {form.watch(LINE_ITEMS)?.length > 0 ? (
              <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[15px]">
                <div className="flex flex-row justify-between items-center">
                  <FormSectionHeader className="">Line Items</FormSectionHeader>
                  <div className="w-full flex items-center justify-end gap-2 h-[38px] mb-5">
                    <Label className="text-[10px]">GL View</Label>
                    <Switch 
                      checked={isEntryView}
                      onCheckedChange={setIsEntryView}
                    />
                    <Label className="text-[10px]">Entry View</Label>
                  </div>
                </div>

                <div className="overflow-x-auto w-full">
                  <div className="min-w-[2500px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="mb-3 rounded">
                          <CustomTableHead className="w-[150px]">
                            Date
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Line Item Text
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Cheque No.
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Amount
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Currency
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Bank Info
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Bank Conf
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Dr/Cr
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            GL A/c
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Sub Ledger
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Combine
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Customer A/c
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Payment Method
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Offset Acct
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            Clearing
                          </CustomTableHead>
                          <CustomTableHead className="w-[100px]">
                            OI Date
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            OI Ref (Doc/LI)
                          </CustomTableHead>
                          <CustomTableHead className="w-[200px]">
                            OI Reference
                          </CustomTableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {form
                          .watch(LINE_ITEMS)
                          ?.map((_: any, index: number) => {
                            const is1001 =
                              form.watch(
                                `${LINE_ITEMS}.${index}.${GL_ACCOUNT}`
                              ) == "1001";
                            const isOffset = _?.[LINE_ITEM_TYPE]?.toLowerCase() == "offset";
                            if (isOffset && isEntryView) return;

                            return (
                              <TableRow
                                key={`row-${index}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                className={cn(
                                  is1001 ? "bg-blue-200 hover:bg-blue-200" : ""
                                )}
                              >
                                <CustomTableCell>
                                  <FormDatePicker
                                    name={`${LINE_ITEMS}.${index}.${DATE}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[DATE]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${DATE}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${LINE_ITEM_TEXT}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[LINE_ITEM_TEXT]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${LINE_ITEM_TEXT}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${CHEQUE_NO}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[CHEQUE_NO]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${CHEQUE_NO}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${AMOUNT}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[AMOUNT]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${AMOUNT}`
                                    )}
                                    startIcon="$"
                                    disabled
                                    variant="float"
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${CURRENCY}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[CURRENCY]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${CURRENCY}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${BANK_INFO}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[BANK_INFO]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${BANK_INFO}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${BANK_CONF}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[BANK_CONF]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${BANK_CONF}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${DRCR}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[DRCR]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${DRCR}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${GL_ACCOUNT}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[GL_ACCOUNT]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${GL_ACCOUNT}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormCheckbox
                                    name={`${LINE_ITEMS}.${index}.${SUB_LEDGER}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[SUB_LEDGER]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${SUB_LEDGER}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormCheckbox
                                    name={`${LINE_ITEMS}.${index}.${COMBINE}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[COMBINE]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${COMBINE}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${CUSTOMER_ACC}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[CUSTOMER_ACC]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${CUSTOMER_ACC}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${PAYMENT_METHOD}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[PAYMENT_METHOD]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${PAYMENT_METHOD}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${OFFSET_ACC}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[OFFSET_ACC]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${OFFSET_ACC}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormCheckbox
                                    name={`${LINE_ITEMS}.${index}.${CLEARING}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[CLEARING]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${CLEARING}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormDatePicker
                                    name={`${LINE_ITEMS}.${index}.${OL_DATE}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[OL_DATE]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${OL_DATE}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${OL_DOC_LI}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[OL_DOC_LI]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${OL_DOC_LI}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                                <CustomTableCell>
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.${OL_REF}`}
                                    control={form.control}
                                    label=""
                                    placeholder="Enter"
                                    error={
                                      (errors[LINE_ITEMS] as any)?.[
                                        `${index}`
                                      ]?.[OL_REF]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${OL_REF}`
                                    )}
                                    disabled
                                    className={cn(is1001 ? "bg-white" : "")}
                                  />
                                </CustomTableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )}
            {isAddPending ? (
              <div className="flex flex-col gap-[10px] bg-white mt-[10px] rounded-lg px-[30px] py-[15px] items-center justify-center">
                <div className="w-[250px]">
                  <Lottie animationData={processingGif} loop={true} />
                </div>
                <div className="w-[450px] text-justify text-[14px]">
                  We are currently processing Cash App records. Please wait,
                  once the processing is complete, the page will refresh
                  automatically.
                </div>
              </div>
            ) : (
              <></>
            )}

            {isAddCashAppError ? (
              <div className="flex flex-col gap-[10px] bg-white mt-[10px] rounded-lg px-[30px] py-[15px] items-center justify-center">
                <div className="w-[250px]">
                  <img src={fileUploadError} />
                </div>
                <div className="w-[450px] text-justify text-[14px]">
                  There is an issue: The total amount does not match the offset
                  amount entered. Please review the Excel file and try again.
                </div>
              </div>
            ) : (
              <></>
            )}

            <div className="flex flex-row justify-end gap-3  py-3">
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
                disabled={isPostPending || isAddPending}
              >
                Cancel
              </Button>

              {hasPostPermission || hasAddPermission ? (
                !isPosted && hasPostPermission && form.watch(LINE_ITEMS)?.length > 0 ? (
                  <Button disabled={isPostPending}>
                    {isPostPending ? <Loader variant="secondary" /> : <></>}{" "}
                    Post
                  </Button>
                ) : !isView && hasAddPermission ? (
                  <Button disabled={isAddPending}>Upload</Button>
                ) : (
                  <></>
                )
              ) : (
                <></>
              )}
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
};

export default AddEdit;

// lastBillingDate for disable run button
